// React Scan
import { scan } from 'react-scan';

import { useState, useEffect, createContext, lazy, Suspense } from 'react';
import { RouterProvider, Navigate } from 'react-router-dom';
import { supabase } from './utilities/supabase';
import { LogoLoader, NotificationProvider, AiProvider } from '@core/components';
import MaintenancePage from './pages/error/Maintenance';
import {
  initializeSentry,
  Sentry,
  sentryCreateBrowserRouter
} from './utilities/sentry';
import UserProfile from './models/UserProfile';

// Lazy load components
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Login = lazy(() => import('./pages/auth/login/Login'));
const Signup = lazy(() => import('./pages/auth/signup/Signup'));
const ForgotPassword = lazy(() =>
  import('./pages/auth/forgotPassword/ForgotPassword')
);
const ResetPassword = lazy(() =>
  import('./pages/auth/resetPassword/ResetPassword')
);
const Loading = lazy(() => import('./pages/auth/loading/Loading'));
const StylePages = lazy(() => import('./pages/styles/Styles'));
const Error = lazy(() => import('./pages/error/Error'));
const MFA = lazy(() => import('./pages/auth/2fa/2FA'));
const XeroCallback = lazy(() =>
  import('./pages/dashboard/settings/tabs/integrations/XeroCallback')
);
const SignupConnect = lazy(() => import('./pages/auth/signup/SignupConnect'));
const Delegate = lazy(() => import('./pages/forms/delegate/Delegate'));

export const UserProfileContext = createContext(null);
export const ThemeContext = createContext(null);

const App = () => {
  const [sessionState, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [theme, setTheme] = useState('light');
  const [isBackendAvailable, setIsBackendAvailable] = useState(true);
  const [showMFA, setShowMFA] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  // Enable performance and dev tools
  if (window.location.hostname.includes('portal.bohsa.co.uk')) {
    initializeSentry({
      enableFeedback: true,
      enableReplays: true,
      enableTracing: true,
      enableProfiling: true
    });
  } else {
    // Enable react-scan profiling for dev
    useEffect(() => {
      scan({ enabled: process.env.NODE_ENV === 'development' });
    }, []);
    //Uncomment to enable sentry!
    // initializeSentry({
    //   enableFeedback: true
    // });
  }

  //Google apis
  useEffect(() => {
    // Load Google Maps JavaScript API
    const script = document.createElement('script');
    script.text = `(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
      key: "${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}",
      v: "weekly"
    });`;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const checkBackendServices = async () => {
    try {
      const { error } = await supabase
        .from('core_entities')
        .select('id')
        .limit(1);
      setIsBackendAvailable(!error);
    } catch (error) {
      console.error('Backend services are unavailable:', error);
      setIsBackendAvailable(false);
    }
  };

  const handleAuthChange = (event, session) => {
    //console.log(event);
    if (
      event === 'SIGNED_IN' ||
      event === 'SIGNED_OUT' ||
      event === 'MFA_CHALLENGE_VERIFIED' ||
      event === 'PASSWORD_RECOVERY'
    ) {
      setSession(session);
      if (event === 'SIGNED_IN' || event === 'MFA_CHALLENGE_VERIFIED') {
        if (
          !sessionState ||
          session.access_token != sessionState.access_token
        ) {
          UserProfile.getByAuthId(session.user.id).then(
            async ({ data, error }) => {
              if (data) {
                const activeProfile =
                  data.find(profile => profile.active) || data[0];

                // Safety check, if they're not active we should mark them as such
                if (activeProfile.active == false) {
                  activeProfile.active = true;
                  await activeProfile.update();
                }
                setUserProfile(activeProfile);
                Sentry.setUser({
                  email: activeProfile.email,
                  fullName: activeProfile.fullName
                });
                const {
                  data: { currentLevel, nextLevel }
                } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

                if (currentLevel === 'aal1' && nextLevel === 'aal2') {
                  // User has MFA enrolled but needs to verify
                  setShowMFA(true);
                } else if (currentLevel === 'aal2' && nextLevel === 'aal2')
                  setShowMFA(false);

                //redirect('/');
              } else if (error) {
                console.error('Error fetching user profile:', error);
              }
            }
          );
        }
      } else if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      } else {
        setUserProfile(null);
        //redirect('/login');
      }
    }
  };

  // Subscribe to auth state changes and handle them via handleAuthChange callback
  // Unsubscribes when component unmounts
  useEffect(() => {
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(handleAuthChange);
    return () => subscription.unsubscribe();
  }, [handleAuthChange]);

  // Check and initialize session on app load
  // - Verifies backend services are available
  // - Gets current session if it exists
  // - Loads user profile data and preferences
  // - Updates Sentry user context
  // - Sets initial theme
  useEffect(() => {
    const checkSession = async () => {
      // First check if backend is available
      await checkBackendServices();
      if (!isBackendAvailable) return;

      // Get current session
      const {
        data: { session }
      } = await supabase.auth.getSession();
      setSession(session);

      // If session exists, load user profile data
      if (session) {
        const { data, error } = await UserProfile.getByAuthId(session.user.id);
        if (data) {
          // Get active profile or fallback to first profile
          const activeProfile = data.find(profile => profile.active) || data[0];
          setUserProfile(activeProfile);

          // Set up Sentry user tracking
          Sentry.setUser({
            email: activeProfile.email,
            fullName: activeProfile.fullName
          });

          // Set theme from user preferences or default to light
          setTheme(activeProfile?.crmPreferences?.theme || 'light');
        } else if (error) {
          console.error('Error fetching user profile:', error);
        }
      }

      setIsLoading(false);
    };

    checkSession();
  }, [isBackendAvailable]);

  const handleAiRequest = async ({
    template,
    selectedText,
    customPrompt,
    customFileData
  }) => {
    try {
      // Your app-specific AI implementation here
      const { data: response } = await supabase.functions.invoke(
        'integrations/add-ai/text-completion',
        {
          body: {
            model: {
              provider: 1,
              id: 31 // gpt-4.1-nano (latest and cheapest as of 17/04/2025)
            },
            details: {
              template: template
            },
            input: {
              content: selectedText,
              prompt: customPrompt
            },
            ...(customFileData && {
              file: {
                name: customFileData.name,
                data: customFileData.data
              }
            })
          }
        }
      );

      return response;
    } catch (error) {
      console.error('Error in AI request:', error);
      throw error;
    }
  };

  const handleVoiceRequest = async audioBlob => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.webm');

    try {
      const { data: response, error } = await supabase.functions.invoke(
        'integrations/add-ai/voice-completion',
        {
          body: formData
        }
      );

      if (error) {
        console.error('Error uploading audio:', error);
        return null;
      }

      return response;
    } catch (error) {
      console.error('Error uploading audio:', error);
      return null;
    }
  };

  if (isLoading) return <LogoLoader logo={'/logo-star.png'} />;
  if (showLoadingScreen) return <Loading />;
  if (!isBackendAvailable) return <MaintenancePage />;
  if (passwordRecovery) return <Navigate to='/resetPassword' />;

  const router = sentryCreateBrowserRouter([
    {
      path: '/',
      errorElement: (
        <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
          <Error />
        </Suspense>
      ),
      children: [
        {
          path: 'connect-xero',
          element: sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <XeroCallback />
            </Suspense>
          ) : (
            <Navigate to='/login' />
          )
        },
        {
          index: true,
          element: !sessionState ? (
            <Navigate to='/login' />
          ) : showMFA ? (
            <Navigate to='/mfa' />
          ) : (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Dashboard />
            </Suspense>
          )
        },
        {
          path: 'login',
          element: !sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Login />
            </Suspense>
          ) : showMFA ? (
            <Navigate to='/mfa' />
          ) : (
            <Navigate to='/' />
          )
        },
        {
          path: 'mfa',
          element: !sessionState ? (
            <Navigate to='/login' />
          ) : !showMFA ? (
            <Navigate to='/' />
          ) : (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <MFA />
            </Suspense>
          )
        },
        {
          path: 'password',
          element: !sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <ForgotPassword />
            </Suspense>
          ) : (
            <Navigate to='/' />
          )
        },
        {
          path: 'resetPassword',
          element: !sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <ResetPassword />
            </Suspense>
          ) : (
            <Navigate to='/' />
          )
        },
        {
          path: 'signup',
          element: !sessionState ? (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Signup />
            </Suspense>
          ) : (
            <Navigate to='/' />
          )
        },
        {
          path: 'signup/:id',
          element: (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <SignupConnect session={sessionState} />
            </Suspense>
          )
        },
        {
          path: 'styles',
          element: !sessionState ? (
            <Navigate to='/login' />
          ) : showMFA ? (
            <Navigate to='/mfa' />
          ) : (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <StylePages />
            </Suspense>
          )
        },
        {
          path: 'delegate/:formType/:spaceId',
          element: (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Delegate />
            </Suspense>
          )
        },
        {
          path: '/*',
          element: !sessionState ? (
            <Navigate to='/login' />
          ) : showMFA ? (
            <Navigate to='/mfa' />
          ) : (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Dashboard />
            </Suspense>
          )
        }
      ]
    }
  ]);

  return (
    <UserProfileContext.Provider value={{ userProfile, setUserProfile }}>
      <NotificationProvider>
        <ThemeContext.Provider value={{ theme, setTheme }}>
          <AiProvider
            handleAiRequest={handleAiRequest}
            handleVoiceRequest={handleVoiceRequest}
          >
            <RouterProvider router={router} />
          </AiProvider>
        </ThemeContext.Provider>
      </NotificationProvider>
    </UserProfileContext.Provider>
  );
};

export default App;
